<template>
  <div class="admins-page">
    <section class="resource-toolbar">
      <div>
        <h2>Administradores</h2>
        <p>Convide e gerencie quem acessa o painel deste local.</p>
      </div>
      <v-btn text color="primary" @click="openInvite">
        <v-icon left size="18">mdi-account-plus-outline</v-icon>
        Adicionar
      </v-btn>
    </section>

    <ListFilters
      v-model="filterDraft"
      :fields="filterFields"
      :loading="loading || loadingInvites"
      @filter="applyFilters"
      @clear="applyFilters"
    />

    <section class="table-panel">
      <BoaruaDataTable
        :headers="headers"
        :items="filteredAdminRows"
        :loading="loading || loadingInvites"
        item-key="row_id"
        class="boarua-table"
      >
        <template v-slot:item.person="{ item }">
          <div class="admin-user">
            <v-avatar size="34">
              <img :src="avatar(item)" />
            </v-avatar>
            <div>
              <strong>{{ item.name || "-" }}</strong>
              <span>{{ item.email || "-" }}</span>
            </div>
          </div>
        </template>

        <template v-slot:item.role="{ item }">
          {{ roleLabel(item.role) }}
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip small :color="statusColor(item.status)" text-color="primary">
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>

        <template v-slot:item.created_at="{ item }">
          {{ formatTableDate(item.created_at) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="action-buttons">
            <template v-if="item.row_type === 'admin'">
              <v-btn icon small :title="isOwner(item) ? 'O proprietário não pode ser editado' : 'Editar'" :disabled="isOwner(item)" @click="openEdit(item)">
                <v-icon size="19">mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                :title="isOwner(item) ? 'O proprietário não pode ser excluído' : 'Excluir'"
                :disabled="isOwner(item)"
                @click="openDelete(item)"
              >
                <v-icon size="19">mdi-trash-can-outline</v-icon>
              </v-btn>
            </template>
            <template v-else>
              <v-btn icon small title="Editar convite" :disabled="item.raw_status !== 'pending'" @click="openEdit(item)">
                <v-icon size="19">mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                title="Reenviar convite"
                :disabled="item.raw_status !== 'pending'"
                :loading="resendingInviteId === item.id"
                @click="resendInvite(item)"
              >
                <v-icon size="19">mdi-email-sync-outline</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                title="Excluir convite"
                :disabled="item.raw_status !== 'pending'"
                @click="openDelete(item)"
              >
                <v-icon size="19">mdi-trash-can-outline</v-icon>
              </v-btn>
            </template>
          </div>
        </template>

        <template v-slot:footer.prepend>
          <v-btn icon small :loading="loading || loadingInvites" title="Atualizar" @click="refreshList">
            <v-icon size="19">mdi-refresh</v-icon>
          </v-btn>
        </template>
      </BoaruaDataTable>
    </section>

    <v-dialog v-model="dialog" max-width="620">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>Novo administrador</strong>
            <span>O convidado receberá um e-mail para concluir o cadastro.</span>
          </div>
        </v-card-title>
        <v-card-text>
          <v-form ref="form">
            <v-text-field v-model="form.name" label="Nome" outlined dense hide-details="auto" class="mb-3" />
            <v-text-field
              v-model="form.email"
              label="E-mail"
              type="email"
              outlined
              dense
              hide-details="auto"
              :rules="[requiredRule, emailRule]"
              class="mb-3"
            />
            <v-select v-model="form.role" :items="roles" label="Perfil" outlined dense hide-details="auto" />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn depressed color="primary" :loading="saving" @click="sendInvite">
            <v-icon left size="18">mdi-send-outline</v-icon>
            Enviar convite
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog" max-width="520">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>{{ editForm.row_type === "invite" ? "Editar convite" : "Editar administrador" }}</strong>
            <span>{{ editForm.name || editForm.email }}</span>
          </div>
        </v-card-title>
        <v-card-text>
          <v-form ref="editForm">
            <v-text-field
              v-model="editForm.name"
              label="Nome"
              outlined
              dense
              :disabled="editForm.row_type === 'admin'"
              hide-details="auto"
              class="mb-3"
            />
            <v-text-field
              v-model="editForm.email"
              label="E-mail"
              type="email"
              outlined
              dense
              :disabled="editForm.row_type === 'admin'"
              hide-details="auto"
              :rules="[requiredRule, emailRule]"
              class="mb-3"
            />
            <v-select v-model="editForm.role" :items="roles" label="Perfil" outlined dense hide-details="auto" class="mb-3" />
            <v-select
              v-if="editForm.row_type === 'admin'"
              v-model="editForm.status"
              :items="adminStatuses"
              label="Status"
              outlined
              dense
              hide-details="auto"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeEdit">Cancelar</v-btn>
          <v-btn depressed color="primary" :loading="savingEdit" @click="saveEdit">
            <v-icon left size="18">mdi-content-save-outline</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="460">
      <v-card class="form-dialog confirm-dialog">
        <v-card-title>
          <div>
            <strong>{{ deleteTitle }}</strong>
            <span>{{ deleteTarget && deleteTarget.row_type === "invite" ? "Confirme a exclusão do convite enviado." : "Confirme a exclusão do administrador." }}</span>
          </div>
        </v-card-title>
        <v-card-text>
          <p>
            {{ deleteMessage }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDelete">Cancelar</v-btn>
          <v-btn depressed color="error" :loading="deleting" @click="confirmDelete">
            <v-icon left size="18">mdi-trash-can-outline</v-icon>
            {{ deleteTarget && deleteTarget.row_type === "invite" ? "Excluir convite" : "Excluir" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import BoaruaDataTable from "@/views/boarua/components/BoaruaDataTable.vue";
import ListFilters from "@/views/boarua/components/ListFilters.vue";
import { applyListFilters, initialListFilters } from "@/views/boarua/helpers/listFilters";

export default {
  name: "BoaruaAssociationAdmins",
  components: { BoaruaDataTable, ListFilters },
  data() {
    return {
      loading: false,
      loadingInvites: false,
      saving: false,
      savingEdit: false,
      deleting: false,
      resendingInviteId: null,
      dialog: false,
      editDialog: false,
      deleteDialog: false,
      filterDraft: initialListFilters(),
      activeFilters: initialListFilters(),
      items: [],
      invites: [],
      form: this.initialForm(),
      editForm: this.initialEditForm(),
      deleteTarget: null,
      roles: [
        { text: "Administrador", value: "admin" },
        { text: "Financeiro", value: "financial" },
        { text: "Operação", value: "operator" },
      ],
      allRoles: [
        { text: "Proprietário", value: "owner" },
        { text: "Administrador", value: "admin" },
        { text: "Financeiro", value: "financial" },
        { text: "Operação", value: "operator" },
      ],
      adminStatuses: [
        { text: "Ativo", value: "active" },
        { text: "Bloqueado", value: "blocked" },
      ],
      headers: [
        { text: "Administrador", value: "person" },
        { text: "Perfil", value: "role" },
        { text: "Status", value: "status" },
        { text: "Criado em", value: "created_at" },
        { text: "", value: "actions", sortable: false, align: "end", width: 130 },
      ],
    };
  },
  computed: {
    filterFields() {
      return [
        { name: "status", label: "Status", type: "select", items: [
          { text: "Ativo", value: "active" },
          { text: "Bloqueado", value: "blocked" },
          { text: "Convite enviado", value: "invite_sent" },
          { text: "Aceito", value: "accepted" },
          { text: "Expirado", value: "expired" },
        ] },
        { name: "role", label: "Perfil", type: "select", items: this.allRoles },
      ];
    },
    adminRows() {
      const activeRows = this.items.map((item) => ({
        row_id: `admin-${item.id}`,
        row_type: "admin",
        id: item.id,
        name: item.user ? item.user.name : "",
        email: item.user ? item.user.email : "",
        role: item.role,
        status: item.status,
        raw_status: item.status,
        created_at: item.created_at || item.createdAt || null,
      }));

      const inviteRows = this.invites.map((item) => ({
        row_id: `invite-${item.id}`,
        row_type: "invite",
        id: item.id,
        name: item.name,
        email: item.email,
        role: item.role,
        status: item.status === "pending" ? "invite_sent" : item.status,
        raw_status: item.status,
        created_at: item.created_at || item.createdAt || null,
      }));

      return [...activeRows, ...inviteRows];
    },
    filteredAdminRows() {
      return applyListFilters(this.adminRows, this.activeFilters, {
        textKeys: ["name", "email", "role", "status"],
        dateKeys: ["created_at"],
      });
    },
    deleteMessage() {
      if (!this.deleteTarget) return "Este acesso será removido.";
      if (this.deleteTarget.row_type === "invite") {
        return `O convite de ${this.deleteTarget.name || this.deleteTarget.email} será cancelado e removido da tabela.`;
      }
      return `${this.deleteTarget.name || "Este administrador"} perderá o acesso ao painel deste local.`;
    },
    deleteTitle() {
      if (this.deleteTarget && this.deleteTarget.row_type === "invite") {
        return "Excluir convite?";
      }
      return "Excluir administrador?";
    },
  },
  mounted() {
    this.load();
    this.loadInvites();
  },
  methods: {
    initialForm() {
      return { name: "", email: "", role: "admin" };
    },
    initialEditForm() {
      return { id: null, row_type: "admin", name: "", email: "", role: "admin", status: "active" };
    },
    requiredRule(value) {
      return !!value || "Campo obrigatório";
    },
    emailRule(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "")) || "Informe um e-mail válido";
    },
    applyFilters(filters) {
      this.activeFilters = Object.assign(initialListFilters(), filters || {});
    },
    async load() {
      this.loading = true;
      try {
        const response = await axios.get("/association-admins", { params: { limit: 100 } });
        this.items = response.data.content.data || response.data.content || [];
      } finally {
        this.loading = false;
      }
    },
    async loadInvites() {
      this.loadingInvites = true;
      try {
        const response = await axios.get("/association-admins/invites", { params: { limit: 100 } });
        this.invites = response.data.content.data || response.data.content || [];
      } finally {
        this.loadingInvites = false;
      }
    },
    async refreshList() {
      await Promise.all([this.load(), this.loadInvites()]);
    },
    openInvite() {
      this.form = this.initialForm();
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    async sendInvite() {
      if (this.$refs.form && !this.$refs.form.validate()) return;
      if (this.form.role === "owner") {
        this.notifyError("Perfil proprietário é reservado para quem criou o local");
        return;
      }

      this.saving = true;
      try {
        const response = await axios.post("/association-admins/invites", this.form);
        const sent = response.data.content && response.data.content.email_sent;
        this.dialog = false;
        await Promise.all([this.load(), this.loadInvites()]);
        this.notifySuccess(sent ? "Convite enviado por e-mail" : "Convite criado, mas o e-mail não foi enviado");
      } catch (error) {
        this.notifyError(error.message || "Não foi possível enviar o convite");
      } finally {
        this.saving = false;
      }
    },
    async resendInvite(item) {
      if (!item || item.raw_status !== "pending") return;

      this.resendingInviteId = item.id;
      try {
        const response = await axios.post(`/association-admins/invites/${item.id}/resend`);
        const sent = response.data.content && response.data.content.email_sent;
        await this.loadInvites();
        this.notifySuccess(sent ? "Convite reenviado por e-mail" : "Reenvio solicitado, mas o e-mail não foi enviado");
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : error.message || "Não foi possível reenviar o convite");
      } finally {
        this.resendingInviteId = null;
      }
    },
    openEdit(item) {
      if (item.row_type === "admin" && this.isOwner(item)) {
        this.notifyError("O proprietário do local não pode ser editado");
        return;
      }
      if (item.row_type === "invite" && item.raw_status !== "pending") {
        this.notifyError("Somente convites pendentes podem ser editados");
        return;
      }

      this.editForm = {
        id: item.id,
        row_type: item.row_type,
        name: item.name,
        email: item.email,
        role: item.role || "admin",
        status: item.raw_status || item.status || "active",
      };
      this.editDialog = true;
    },
    closeEdit() {
      this.editDialog = false;
      this.editForm = this.initialEditForm();
    },
    async saveEdit() {
      if (!this.editForm.id) return;
      if (this.$refs.editForm && !this.$refs.editForm.validate()) return;
      if (this.editForm.role === "owner") {
        this.notifyError("Perfil proprietário é reservado para quem criou o local");
        return;
      }

      this.savingEdit = true;
      try {
        const isInvite = this.editForm.row_type === "invite";
        if (isInvite) {
          await axios.patch(`/association-admins/invites/${this.editForm.id}`, {
            name: this.editForm.name,
            email: this.editForm.email,
            role: this.editForm.role,
          });
        } else {
          await axios.patch(`/association-admins/${this.editForm.id}`, {
            role: this.editForm.role,
            status: this.editForm.status,
          });
        }
        this.closeEdit();
        await Promise.all([this.load(), this.loadInvites()]);
        this.notifySuccess(isInvite ? "Convite atualizado" : "Administrador atualizado");
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : error.message || "Não foi possível atualizar o acesso");
      } finally {
        this.savingEdit = false;
      }
    },
    openDelete(item) {
      if (item.row_type === "admin" && this.isOwner(item)) {
        this.notifyError("O proprietário do local não pode ser excluído");
        return;
      }
      if (item.row_type === "invite" && item.raw_status !== "pending") {
        this.notifyError("Somente convites pendentes podem ser excluídos");
        return;
      }

      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    closeDelete() {
      this.deleteDialog = false;
      this.deleteTarget = null;
    },
    async confirmDelete() {
      if (!this.deleteTarget) return;

      this.deleting = true;
      try {
        const endpoint = this.deleteTarget.row_type === "invite"
          ? `/association-admins/invites/${this.deleteTarget.id}`
          : `/association-admins/${this.deleteTarget.id}`;
        await axios.delete(endpoint);
        this.closeDelete();
        await Promise.all([this.load(), this.loadInvites()]);
        this.notifySuccess("Acesso excluído");
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : error.message || "Não foi possível excluir o acesso");
      } finally {
        this.deleting = false;
      }
    },
    avatar(item) {
      const name = item && item.name ? item.name : "BOARUA";
      return `https://ui-avatars.com/api/?background=057a3d&color=fff&name=${encodeURIComponent(name)}`;
    },
    roleLabel(role) {
      const found = this.allRoles.find((item) => item.value === role);
      return found ? found.text : role || "-";
    },
    isOwner(item) {
      return String(item && item.role ? item.role : "") === "owner";
    },
    statusLabel(status) {
      const labels = {
        active: "Ativo",
        pending: "Convite enviado",
        invite_sent: "Convite enviado",
        invited: "Convite enviado",
        accepted: "Aceito",
        blocked: "Bloqueado",
        expired: "Expirado",
      };
      return labels[status] || status || "-";
    },
    statusColor(status) {
      if (status === "active") return "offWhite";
      if (["pending", "invite_sent", "invited"].includes(status)) return "secondary";
      return "lightImportant";
    },
    formatTableDate(value) {
      if (!value || value === 0 || value === "0") return "-";
      const formatter = this.$options.filters && this.$options.filters.defaultDate;
      const formatted = formatter ? formatter(value) : value;
      return formatted && formatted !== "Invalid date" ? formatted : "-";
    },
    notifySuccess(message) {
      if (this.$dialog && this.$dialog.notify) this.$dialog.notify.success(message, { position: "top-right", timeout: 3000 });
    },
    notifyError(message) {
      if (this.$dialog && this.$dialog.notify) this.$dialog.notify.error(message, { position: "top-right", timeout: 4000 });
    },
  },
};
</script>

<style scoped lang="scss">
.resource-toolbar {
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;

  h2 {
    margin: 0;
    color: #111827;
    font-size: 22px;
  }

  p {
    margin: 4px 0 0;
    color: #66736b;
    font-size: 13px;
  }
}

.table-panel {
  overflow: hidden;
  background: #ffffff;
  border-radius: 16px;
}

.section-title {
  padding: 18px 20px 0;

  h3 {
    margin: 0;
    color: #111827;
    font-size: 18px;
  }
}

.admin-user {
  display: flex;
  align-items: center;
  gap: 10px;

  strong,
  span {
    display: block;
  }

  span {
    color: #66736b;
    font-size: 12px;
  }
}

.action-buttons {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.form-dialog {
  border-radius: 16px;

  .v-card__title {
    padding: 22px 24px 10px;

    strong,
    span {
      display: block;
    }

    strong {
      color: #111827;
      font-size: 20px;
    }

    span {
      margin-top: 4px;
      color: #66736b;
      font-size: 13px;
      font-weight: 400;
    }
  }
}

.confirm-dialog {
  border-radius: 16px;

  .v-card__title {
    padding: 22px 24px 8px;
    color: #111827;
    font-size: 20px;
    font-weight: 900;
  }

  .v-card__text {
    padding: 0 24px 12px;

    p {
      margin: 0;
      color: #66736b;
      font-size: 14px;
      line-height: 1.45;
    }
  }
}

@media (max-width: 860px) {
  .resource-toolbar {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
